import { CommonModule } from '@angular/common';
import { Component, inject, Inject, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { AccordionModule } from 'primeng/accordion';
import { FilterMatchMode, FilterMetadata, FilterService, MessageService, SelectItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DialogModule } from 'primeng/dialog';
import { FloatLabelModule } from 'primeng/floatlabel';
import { SelectModule } from 'primeng/select';
import { Table, TableLazyLoadEvent, TableModule } from 'primeng/table';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';
import { TreeSelectModule } from 'primeng/treeselect';

import { Settings, SETTINGS } from '../../../app.settings';
import { getItemDescriptionColumn, isStringEmpty, isStringEqual, toShortDate } from '../../../common/functions';
import { notifyAndTrackError } from '../../../common/error';
import { ProcessingComponent } from '../../../components/processing/processing.component';
import { AppInitService, IInsectaPermissions } from '../../../services/app-init.service';
import { AppInsightsService } from '../../../services/app-insights.service';
import { IAppUser, ISearchRequest, ISearchSort, ISearchRequestFilter, ISearchFilterValue } from '../../../services/insecta';

import { MSGraphService, ProfileType } from '../../../services/msgraph.service';
import { NewOrderDialogComponent } from '../../orders/new-order-dialog/new-order-dialog.component';
import { RequesterCalendarComponent } from './requester-calendar/requester-calendar.component';
import { RequesterDashboardService } from '../../../services/insecta/dashboard/requester-dashboard.service';

@Component({
  selector: 'app-requester-dashboard',
  standalone: true,
  imports: [
    ButtonModule,
    CommonModule,
    TableModule,
    RouterModule,
    FloatLabelModule,
    CardModule,
    DialogModule,
    FormsModule,
    SelectModule,
    ToggleSwitchModule,
    TooltipModule,
    TreeSelectModule,
    NewOrderDialogComponent,
    RequesterCalendarComponent,
    AccordionModule,
    ProcessingComponent
  ],
  templateUrl: './requester-dashboard.component.html'
})
export class RequesterDashboardComponent implements OnInit {
  @ViewChild('requesterCalendar') requesterCalendar!: RequesterCalendarComponent;
  @ViewChild('dt') dt!: Table;

  appUser!: IAppUser;
  permissions!: IInsectaPermissions;
  userSettingsJson: { [key: string]: any } = {};

  orderGroups: any[] = [];
  expandedRows: { [key: string]: boolean } = {};
  requesterEmail: any;

  pageVars: any = {
    currentUser: ``,
    isEditing: false,
    isReady: false,
    showUnsavedChangesDialog: false,
    pageId: `dashboard/requester`
  };

  pageSize = 25;
  currentPage = 1;
  paginatedData: any[] = [];

  totalRecords = 0;
  totalPages = 0;
  startRecord = 0;
  endRecord = 0;

  columns: string[] = [
    'OrderId',
    'OrderSequence',
    'OrderName',
    'RequesterEmail',
    'RequesterName',
    'ItemSequence',
    'ItemStatus',
    'ItemType',
    'ItemDescription',
    'ItemDescription_C',
    'ItemDescription_S',
    'ItemDescription_CS',
    'FirstDelivery',
    'LastDelivery',
    'InstanceCount'
  ];

  orders: any[] = [];
  cols: any[] = [];
  skip: number = 0;
  take: number = 25;
  totalCount: number = 0;
  deliveredCount: string = '';
  showAll: boolean = false;

  activeColumnFilters: Set<string> = new Set<string>();
  filtersActive: boolean = false;

  customMatchModeOptions: SelectItem[] = [
    { label: 'Starts With', value: FilterMatchMode.STARTS_WITH },
    { label: 'Contains', value: FilterMatchMode.CONTAINS },
    { label: 'Not Contains', value: FilterMatchMode.NOT_CONTAINS },
    { label: 'Ends With', value: FilterMatchMode.ENDS_WITH },
    { label: 'Equals', value: FilterMatchMode.EQUALS },
    { label: 'Not Equals', value: FilterMatchMode.NOT_EQUALS },
    { label: 'Is Empty', value: 'isEmpty' },
    { label: 'Has Value', value: 'isNotEmpty' }
  ];
  dateMatchModeOptions: SelectItem[] = [
  { label: 'Date Is', value: FilterMatchMode.DATE_IS },
  { label: 'Date Is Not', value: FilterMatchMode.DATE_IS_NOT },
  { label: 'Date Before', value: FilterMatchMode.DATE_BEFORE },
  { label: 'Date After', value: FilterMatchMode.DATE_AFTER },
  { label: 'Is Empty', value: 'isEmpty' },
  { label: 'Has Value', value: 'isNotEmpty' }
];

numericMatchModeOptions: SelectItem[] = [
  { label: 'Equals', value: FilterMatchMode.EQUALS },
  { label: 'Not Equals', value: FilterMatchMode.NOT_EQUALS },
  { label: 'Less Than', value: FilterMatchMode.LESS_THAN },
  { label: 'Less Than or Equal', value: FilterMatchMode.LESS_THAN_OR_EQUAL_TO },
  { label: 'Greater Than', value: FilterMatchMode.GREATER_THAN },
  { label: 'Greater Than or Equal', value: FilterMatchMode.GREATER_THAN_OR_EQUAL_TO },
  { label: 'Is Empty', value: 'isEmpty' },
  { label: 'Has Value', value: 'isNotEmpty' }
];
  
  requesterDashboardService: RequesterDashboardService = inject(RequesterDashboardService);
  searchRequest: ISearchRequest = {
    filters: [],
    sorts: [],
    skip: this.skip,
    take: this.take
  };

  siteName: string = '';
  itemDescName: string = 'ItemDescription';

  constructor(
    private appInit: AppInitService,
    private appInsights: AppInsightsService,
    private graphService: MSGraphService,
    private router: Router,
    private msgService: MessageService,
    private filterService: FilterService,
    @Inject(SETTINGS) private settings: Settings
  ) {
    this.pageVars.pageId = `${settings.environment}:Dashboard:Requesterdashboard`;
    appInsights.trackPageView(this.pageVars.pageId, `/dashboard/requesterdashboard`);
  }

  ngOnInit(): void {
    this.pageVars.isReady = false;
    this.appUser = this.appInit.appUser;

    this.filterService.register('isEmpty', (value: any): boolean => {
      return value === undefined || value === null || (typeof value === 'string' && value.trim() === '');
    });

    this.filterService.register('isNotEmpty', (value: any): boolean => {
      return value !== undefined && value !== null && (typeof value === 'string' && value.trim() !== '');
    });

    if (this.appUser) {
      this.userSettingsJson =
        !isStringEmpty(this.appUser.userSettingsJson) || isStringEqual(this.appUser.userSettingsJson, '{}')
          ? JSON.parse(this.appUser.userSettingsJson)
          : {};

      this.itemDescName = getItemDescriptionColumn(this.userSettingsJson);
      this.siteName = this.userSettingsJson['Default Site'] || '';

      firstValueFrom(this.graphService.getMyProfile$())
        .then(async (profile: ProfileType) => {
          this.pageVars.profile = profile;
          this.pageVars.currentUser = profile.mail;
          await this.fetchDeliveredItems();
          if (this.dt) {
            this.dt.onLazyLoad.emit(this.dt.createLazyLoadMetadata());
          }
        })
        .catch((err) => {
          notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'RequesterDashboardComponent', action: 'InitLoadProfile' });
          this.pageVars.isReady = true;
        });
    }
    this.initializeColumns();
  }

  initializeColumns() {
    const descField = this.itemDescName || 'ItemDescription';
    this.cols = [
      { field: 'OrderSequence', header: 'Order Sequence', type: 'text', template: 'link' },
      { field: 'OrderName', header: 'Order Name', type: 'text' },
      { field: 'RequesterName', header: 'Requester Name', type: 'text'},
      { field: 'ItemSequence', header: 'Item Sequence', type: 'text'},
      { field: 'ItemStatus', header: 'Item Item Status', type: 'text'},
      { field: 'ItemType', header: 'Item Item Type', type: 'text'},
      { field: descField, header: 'Item Description', type: 'text', template: 'tooltip', style: { 'min-width': '250px' } },
      { field: 'FirstDelivery', header: 'First Delivery', type: 'date' },
      { field: 'LastDelivery', header: 'Last Delivery', type: 'date' },
      { field: 'InstanceCount', header: 'Instance Count', type: 'numeric' }
    ];
  }

  async executeSearch() {
    const request = this.searchRequest;
    request.columns = [...this.columns, this.itemDescName];

    try {
      const response = await firstValueFrom(this.requesterDashboardService.getTable(request));
      this.totalCount = response?.totalCount || 0;
      this.orders = JSON.parse(response?.results ?? `[]`).map((order: any) => ({ ...order }));
    } catch (err) {
      notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'RequesterDashboardComponent', action: 'ExecuteSearch' });
    } finally {
      const calendarFilters = this.searchRequest.filters.filter(
        (f) => !f.values.some((v) => v.operator === 'ISEMPTY' || v.operator === 'ISNOTEMPTY')
      );
      this.requesterCalendar.loadCalendar([...calendarFilters]);
      this.pageVars.isReady = true;
    }
  }

  async fetchDeliveredItems() {
    const deliveredRequest: ISearchRequest = {
      filters: [],
      sorts: []
    };

    if (this.pageVars.currentUser) {
      deliveredRequest.filters.push(<ISearchRequestFilter>{
        fieldname: `SiteName`,
        logic: `and`,
        values: [<ISearchFilterValue>{ operator: `equals`, value: this.siteName }]
      });
      deliveredRequest.filters.push(<ISearchRequestFilter>{
        fieldname: `RequesterEmail`,
        logic: `and`,
        values: [<ISearchFilterValue>{ operator: `equals`, value: this.pageVars.currentUser }]
      });
    }

    firstValueFrom(this.requesterDashboardService.getReceiveCount(deliveredRequest))
      .then((response) => {
        this.deliveredCount = `${response?.totalCount ?? ''}`;
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'RequesterDashboardComponent', action: 'FetchDeliveredItems' });
        this.deliveredCount = '!';
      });
  }

  async toggleShowAll() {
    this.pageVars.isReady = false;

    if (this.showAll === true) {
      this.searchRequest.filters = this.searchRequest.filters.filter((x) => isStringEqual(x.fieldname, 'RequesterEmail') === false);
    } else {
      this.searchRequest.filters.push(<ISearchRequestFilter>{
        fieldname: 'RequesterEmail',
        logic: 'and',
        values: [<ISearchFilterValue>{ operator: 'equals', value: this.pageVars.currentUser }]
      });
    }

    await this.executeSearch();
    this.pageVars.isReady = true;
  }

  async handleTableEvents(event: TableLazyLoadEvent) {
    if (!this.pageVars.currentUser) {
      return;
    }

    this.pageVars.isReady = false;
    this.activeColumnFilters.clear();
    this.searchRequest.filters = [];

    if (this.showAll === false) {
      this.searchRequest.filters.push(<ISearchRequestFilter>{
        fieldname: 'RequesterEmail',
        logic: 'and',
        values: [<ISearchFilterValue>{ operator: 'equals', value: this.pageVars.currentUser }]
      });
    }

    this.columns.forEach((columnName) => {
      const columnFilters = event.filters![columnName] as FilterMetadata[];
      if (columnFilters && columnFilters.length > 0) {
        const tableFilter = <ISearchRequestFilter>{
          fieldname: columnName,
          logic: columnFilters[0].operator,
          values: []
        };
        columnFilters.forEach((filterValue) => {
          if (filterValue.matchMode === 'isEmpty' || filterValue.matchMode === 'isNotEmpty') {
            tableFilter.values.push(<ISearchFilterValue>{
              operator: filterValue.matchMode.toUpperCase(),
              value: ''
            });
          } else if (filterValue.value !== null && filterValue.value !== undefined && filterValue.value !== '') {
            tableFilter.values.push(<ISearchFilterValue>{
              operator: filterValue.matchMode || `contains`,
              value: filterValue.matchMode?.startsWith(`date`) ? toShortDate(filterValue.value) : `${filterValue.value}`
            });
          }
        });
        if (tableFilter.values.length > 0) {
          this.searchRequest.filters.push(tableFilter);
          this.activeColumnFilters.add(columnName);
        }
      }
    });

    this.searchRequest.sorts = [];
    if (event.sortField && event.sortField !== null) {
      this.searchRequest.sorts.push(<ISearchSort>{
        name: event.sortField,
        direction: event.sortOrder === 1 ? 'ASC' : 'DESC'
      });
    } else {
      this.searchRequest.sorts.push(<ISearchSort>{
        name: 'OrderSequence',
        direction: 'DESC'
      });
    }

    this.searchRequest.skip = event.first || 0;
    this.searchRequest.take = event.rows || 25;
    this.filtersActive = this.activeColumnFilters.size > 0 || (event.sortField !== null && event.sortField !== undefined);
    
    await Promise.all([this.fetchDeliveredItems(), this.executeSearch()]);
  }

  getColumnHeaderStyle(columnName: string): any {
    return this.activeColumnFilters.has(columnName) ? { 'background-color': '#e0f7fa', 'border-bottom': '2px solid #006064' } : {};
  }


  clearFilters(table: Table) {
  table.sortField = null;
  table.sortOrder = 1;
  if (table.filters) {
    Object.keys(table.filters).forEach((key) => {
      const col = this.cols?.find((c: any) => c.field === key);
      const filters = table.filters[key] as any[];
      if (Array.isArray(filters)) {
        filters.forEach((f) => {
          if (f.matchMode === 'isEmpty' || f.matchMode === 'isNotEmpty') {
          if (col?.type === 'date') {
            f.matchMode = 'dateIs';
          } else if (col?.type === 'numeric') {
            f.matchMode = 'equals';
          } else {
            f.matchMode = 'contains';
          }
            f.value = null;
          }
        });
      }
    });
  }
  table.clear();
  this.activeColumnFilters.clear();
  this.filtersActive = false;

  try {
    const stateKey = 'requester-dashboard-table';
    const storedState = localStorage.getItem(stateKey);
    if (storedState) {
      const state = JSON.parse(storedState);
      delete state.sortField;
      delete state.sortOrder;
      delete state.filters;
      localStorage.setItem(stateKey, JSON.stringify(state));
    }
  } catch (e) {
    console.error('Error updating local storage in clearFilters', e);
  }

  setTimeout(() => {
    this.searchRequest.filters = [];
    if (this.showAll === false) {
      this.searchRequest.filters.push(<ISearchRequestFilter>{
        fieldname: 'RequesterEmail',
        logic: 'and',
        values: [<ISearchFilterValue>{ operator: 'equals', value: this.pageVars.currentUser }]
      });
    }
    this.searchRequest.sorts = [{ name: 'OrderSequence', direction: 'DESC' }];
    this.searchRequest.skip = 0;
    this.searchRequest.take = this.take;
    this.executeSearch();
  }, 100);
}
  resetPage(table: Table) {
    table.clear();
    table.clearState();
    window.location.reload();
  }

  receiveInstances() {
    this.router.navigate(['requesterdashboard/receive']);
  }
}